import { usePricingPlans } from "@/hooks/useProducts";
import { AlertCircle, DollarSign } from "lucide-react";
import { useCallback } from "react";

import { PageLoadingSpinner } from "../PageLoadingSpinner";
import { EnterpriseCard } from "./EnterpriseCard";
import { PlanCard } from "./PlanCard";

export interface PricingPlan {
  pricingId: string;
  name: string;
  description: string;
  tier_order: number;
  price: number;
  currency: string;
  billingCycle: string;
  pages: number;
  overusagePricing?: number;
  isPopular?: boolean;
  features?: string[];
}

interface PricingSectionProps {
  heading?: string;
  description?: string;
  activePriceProductId?: string | null;
  isLoadingActiveSubscription?: boolean;
  onPlanSelect?: (
    plan: PricingPlan,
    options?: { action: "subscribe" | "upgrade" | "downgrade" }
  ) => void | Promise<void>;
  loadingPlanId?: string | null;
  showFeatures?: boolean;
  onManageSubscription?: () => void | Promise<void>;
  isLoadingManageSubscription?: boolean;
  disabled?: boolean;
  /** When set, Enterprise "Contact us" uses mailto instead of /waitlist (subscription page, landing). */
  enterpriseContactHref?: string;
  isFreeTrial?: boolean;
}

// Formatting helper functions
const formatPrice = (amount: number, currency: string, billingCycle: string): string => {
  const formattedAmount = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

  const cycleText = billingCycle === "monthly" ? "month" : billingCycle === "yearly" ? "year" : "";
  return `${formattedAmount}${cycleText ? ` / ${cycleText}` : ""}`;
};

const formatPages = (pages: number): string => {
  return pages > 0 ? `Includes ${pages} pages` : "";
};

const formatOverusagePrice = (amount: number, currency: string): string => {
  if (!amount) return "";
  const formattedAmount = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
  return `${formattedAmount} / page`;
};

export const PricingSection = ({
  heading = "Pricing",
  description = "Everything you need to supercharge your injury record review processes.",
  activePriceProductId = null,
  isLoadingActiveSubscription = false,
  onPlanSelect,
  loadingPlanId = null,
  showFeatures = false,
  onManageSubscription,
  isLoadingManageSubscription = false,
  disabled = false,
  enterpriseContactHref,
  isFreeTrial = false,
}: PricingSectionProps) => {
  const { plans, isLoading: loading, isError } = usePricingPlans();

  const error = isError ? "Failed to load pricing information. Please try again later." : null;

  const isCurrentPlan = useCallback(
    (priceId: string) => Boolean(activePriceProductId && activePriceProductId === priceId),
    [activePriceProductId]
  );

  const hasActiveSubscription = Boolean(activePriceProductId);

  // Active plan is one of the tiered plans (Starter / Plus / Pro) — enables upgrade/downgrade on other tiered plans
  const activePlan = useCallback(
    () => plans.find((p) => p.pricingId === activePriceProductId),
    [plans, activePriceProductId]
  );
  const activePlanIsTiered = Boolean(activePlan());
  const activeTierOrder = activePlan()?.tier_order ?? null;

  // Check if active subscription doesn't match any of the displayed plans (i.e. Enterprise)
  const isEnterpriseActivated = useCallback(() => {
    if (!activePriceProductId || plans.length === 0) return false;
    return !plans.some((plan) => plan.pricingId === activePriceProductId);
  }, [activePriceProductId, plans]);

  const enterpriseIsActivated = isEnterpriseActivated();

  if (loading || isLoadingActiveSubscription) {
    return (
      <div className="absolute left-[-50%] top-[-50%] translate-x-[-50%] translate-y-[-50%]">
        <PageLoadingSpinner />
      </div>
    );
  }

  if (error && plans.length === 0) {
    return (
      <section className="bg-background pb-20 pt-10">
        <div className="container mx-auto flex min-h-[400px] items-center justify-center px-4">
          <div className="flex max-w-md flex-col items-center gap-4 text-center">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <h2 className="text-2xl font-bold text-foreground">Unable to Load Plans</h2>
            <p className="text-muted-foreground">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 rounded-lg bg-primary px-6 py-2 text-white hover:bg-primary-hover"
            >
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-background pb-20 pt-10">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-8 flex max-w-4xl flex-col items-center gap-4 text-center">
          <div className="mb-2 flex h-20 w-20 items-center justify-center rounded-full bg-primary-subtle">
            <DollarSign className="h-10 w-10 text-primary" />
          </div>
          <h2 className="text-4xl font-bold text-foreground">{heading}</h2>
          <p className="max-w-2xl text-muted-foreground">{description}</p>
          {isFreeTrial && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-warning-border bg-warning-subtle px-4 py-1.5 text-sm font-medium text-warning-text">
              You are currently on a free trial
            </span>
          )}
        </div>
        <div className="mx-auto flex max-w-7xl flex-col flex-wrap items-center justify-center gap-x-6 gap-y-12 pt-6 md:flex-row md:items-stretch">
          {plans.map((plan) => {
            const isPlanCurrent = isCurrentPlan(plan.pricingId);
            const isLoading = loadingPlanId === plan.pricingId;
            const isPlanDisabled = disabled && !isPlanCurrent;
            return (
              <PlanCard
                key={plan.pricingId}
                plan={plan}
                isPlanCurrent={isPlanCurrent}
                hasActiveSubscription={hasActiveSubscription}
                activePlanIsTiered={activePlanIsTiered}
                activeTierOrder={activeTierOrder}
                isLoading={isLoading}
                isLoadingManageSubscription={isLoadingManageSubscription}
                showFeatures={showFeatures}
                onPlanSelect={onPlanSelect}
                onManageSubscription={onManageSubscription}
                formatPrice={formatPrice}
                formatPages={formatPages}
                formatOverusagePrice={formatOverusagePrice}
                disabled={isPlanDisabled}
              />
            );
          })}
          <EnterpriseCard
            isActivated={enterpriseIsActivated}
            onManageSubscription={onManageSubscription}
            isLoadingManageSubscription={isLoadingManageSubscription}
            enterpriseContactHref={enterpriseContactHref}
          />
        </div>
      </div>
    </section>
  );
};
